import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CAREERS, INTERESTS } from './profile-setup.constants';
import { UserService } from '../../../services/user.service';
import { ProfileService } from '../../../services/profile.service';
import { forkJoin } from 'rxjs';
import { Interest } from '../../../models/interest';
import { UserAvatar } from '../user-avatar/user-avatar';

/**
 * Multi-step onboarding dialog for first-time profile setup.
 * Collects preferred name, career, interests, and photo, then
 * persists them across user and profile records on finish.
 *
 * NOTE: given name, last name, and grade level are NOT editable here —
 * those are set elsewhere (e.g. account creation/roster import) and
 * must never be overwritten by student-facing onboarding.
 */
@Component({
  selector: 'app-profile-setup',
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, UserAvatar],
  templateUrl: './profile-setup.html',
  styleUrl: './profile-setup.css',
})
export class ProfileSetup {
  constructor(
    /** Reference to this dialog instance, used to close it on successful submit. */
    private dialogRef: MatDialogRef<ProfileSetup>,
    /** Service for fetching and updating the current user record. */
    private userService: UserService,
    /** Service for creating the authenticated user's profile record. */
    private profileService: ProfileService,
    /** Shows a dismissible popup for submit errors (e.g. a rejected preferred name). */
    private snackBar: MatSnackBar,
  ) {}

  /** Current active step index in the onboarding flow. */
  currentStep = 1;

  /** Total steps in the onboarding flow.
   * @readonly
   */
  readonly steps = [1, 2, 3, 4];

  /** Whether a submit request is currently in flight. */
  isSubmitting = false;

  /** Error message displayed when a submit or data fetch fails. */
  error = '';

  /** Available career options sourced from profile-setup constants. */
  careers = CAREERS;

  /** Available interest options sourced from profile-setup constants. */
  interests = INTERESTS;

  /**
   * Returns the user's initials from first/last name, falling back to '?'.
   * @returns {string}
   */
  get initials(): string {
    const f = (this.profile.preferredName || this.profile.firstName)?.[0] ?? '';
    const l = this.profile.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  /**
   * Collected profile data bound to the onboarding form steps.
   * firstName/lastName are populated from the existing user record for
   * display only (Step 4 preview) — they are never edited or resubmitted.
   */
  profile = {
    firstName: '',
    lastName: '',
    preferredName: '',
    careerId: null as number | null,
    interests: [] as Interest[],
    /** Base64 data URL of the selected photo, or empty string if none chosen. */
    photoUrl: '',
  };

  /**
   * Calculates profile completion as a percentage based on how many
   * of the key fields have been filled in. Sent to the backend on submit.
   * @returns {number} Completion percentage (0–100).
   */
  get profileCompletePercent(): number {
    const fields = [
      this.profile.preferredName,
      this.profile.careerId ? 'yes' : '',
      this.profile.interests.length > 0 ? 'yes' : '',
    ];
    const filled = fields.filter(Boolean).length;
    return Math.round((filled / fields.length) * 100);
  }

  /**
   * Adds the interest if not already selected, removes it if it is.
   * @param {Interest} interest - The interest to toggle.
   * @returns {void}
   */
  toggleInterest(interest: Interest): void {
    const idx = this.profile.interests.indexOf(interest);
    idx > -1 ? this.profile.interests.splice(idx, 1) : this.profile.interests.push(interest);
  }

  /**
   * Advances to the next step, or calls finish() if already on the last step.
   * @returns {void}
   */
  next(): void {
    this.currentStep < this.steps.length ? this.currentStep++ : this.finish();
  }

  /**
   * Returns to the previous step if not already on step 1.
   * @returns {void}
   */
  prev(): void {
    if (this.currentStep > 1) this.currentStep--;
  }

  /**
   * Programmatically triggers the hidden file input for photo selection.
   * @returns {void}
   */
  triggerUpload(): void {
    document.getElementById('photoInput')?.click();
  }

  /**
   * Reads the selected photo file and stores it as a base64 data URL in `profile.photoUrl`.
   * @param {Event} event - The change event from the file input.
   * @returns {void}
   */
  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      const reader = new FileReader();
      reader.onload = (e) => (this.profile.photoUrl = e.target?.result as string);
      reader.readAsDataURL(input.files[0]);
    }
  }

  /**
   * Submits the completed profile. Fetches the student record by user ID first,
   * then runs updateUser and createProfile in parallel via forkJoin.
   * Deliberately does NOT send first_name, last_name, or grade_level —
   * those fields are locked and must not be modifiable through onboarding.
   * Closes the dialog with `{ success: true }` on completion.
   * @returns {void}
   */
  finish(): void {
    const user = this.userService.currentUser;
    if (!user) {
      this.error = 'Could not identify user. Please log in again.';
      return;
    }

    this.isSubmitting = true;
    this.error = '';

    const updateUser$ = this.userService.updateUser(user.id, {
      preferred_name: this.profile.preferredName?.trim() || undefined,
      is_active: true,
    } as any);

    const createProfile$ = this.profileService.createMyProfile({
      bio: '',
      profile_picture_path: this.profile.photoUrl || null,
      career_id: this.profile.careerId,
      profile_complete_percent: this.profileCompletePercent,
    });

    forkJoin([updateUser$, createProfile$]).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.dialogRef.close({ success: true });
      },
      error: (err) => {
        console.error('Profile setup failed:', err);
        const message =
          err?.error?.message || err?.error?.error || 'Something went wrong. Please try again.';
        this.error = message;
        this.isSubmitting = false;
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }
}
